<template>
  <div v-click-outside="close" class="filter-dropdown" :class="{ open }">
    <button class="filter-dropdown-trigger" :class="{ 'has-value': hasValue }"
      :title="`Filter by ${placeholder}`" :aria-label="`Filter by ${placeholder}`" @click="open = !open">
      <template v-if="multiple">
        <span class="filter-dropdown-trigger-label">{{ placeholder }}</span>
      </template>
      <span v-else class="filter-dropdown-sizer">
        <!-- Ghost reserves the width of the widest possible label so the trigger
             doesn't resize/shift when the selected value changes. -->
        <span class="filter-dropdown-ghost" aria-hidden="true">{{ widestLabel }}</span>
        <span class="filter-dropdown-current">{{ triggerLabel }}</span>
      </span>
      <ChevronUpIcon v-if="open" class="filter-dropdown-arrow" />
      <ChevronDownIcon v-else class="filter-dropdown-arrow" />
    </button>
    <span v-if="multiple && selectedArray.length" class="filter-dropdown-badge">{{ selectedArray.length }}</span>
    <ul v-if="open" class="filter-dropdown-list">
      <li v-if="showAll && !multiple" class="filter-dropdown-item" :class="{ active: !hasValue }" @click="selectAll">
        all
      </li>
      <li v-for="option in normalizedOptions" :key="option.value"
        class="filter-dropdown-item" :class="{ active: isActive(option.value), 'is-multi': multiple }"
        @click="select(option.value)">
        <span v-if="multiple" class="filter-dropdown-box" :class="{ checked: isActive(option.value) }" aria-hidden="true" />
        <span class="filter-dropdown-text" :title="option.text">{{ option.text }}</span>
        <span v-if="option.hint" class="filter-dropdown-hint">{{ option.hint }}</span>
      </li>
      <!-- Extra controls under the options; clicks inside don't close the list -->
      <li v-if="$slots.footer" class="filter-dropdown-footer">
        <slot name="footer" />
      </li>
      <li v-if="multiple && hasValue" class="filter-dropdown-item filter-dropdown-clear" @click="selectAll">
        Clear
        <span class="filter-dropdown-clear-x" aria-hidden="true">×</span>
      </li>
    </ul>
  </div>
</template>

<script>
import ChevronDownIcon from '@/assets/icons/chevron-down.vue'
import ChevronUpIcon from '@/assets/icons/chevron-up.vue'

export default {
  name: 'FilterDropdown',
  components: { ChevronDownIcon, ChevronUpIcon },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value() }
        document.addEventListener('click', el._clickOutside)
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },

  props: {
    // String in single mode; array of strings in multiple mode.
    modelValue: { type: [String, Array], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'All' },
    showAll: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false }
  },

  emits: ['update:modelValue'],

  data() {
    return { open: false }
  },

  computed: {
    // Accept both plain strings (e.g. ['available','sold']) and {text,value}
    // pairs (tag id/label), normalizing to {text,value} internally.
    normalizedOptions() {
      return this.options.map(o =>
        (o && typeof o === 'object') ? { text: o.text, value: o.value, hint: o.hint } : { text: o, value: o }
      )
    },
    selectedArray() {
      return Array.isArray(this.modelValue) ? this.modelValue : []
    },
    hasValue() {
      return this.multiple ? this.selectedArray.length > 0 : !!this.modelValue
    },
    labelFor() {
      const map = new Map(this.normalizedOptions.map(o => [o.value, o.text]))
      return (value) => map.get(value) ?? value
    },
    // Longest of the placeholder + all option labels — used to reserve the
    // single-select trigger width so it doesn't shift on selection.
    widestLabel() {
      const candidates = [this.placeholder, ...this.normalizedOptions.map(o => String(o.text))]
      return candidates.reduce((a, b) => (b.length > a.length ? b : a), '')
    },
    triggerLabel() {
      if (!this.multiple) return this.modelValue ? this.labelFor(this.modelValue) : this.placeholder
      const count = this.selectedArray.length
      if (count === 0) return this.placeholder
      if (count === 1) return this.labelFor(this.selectedArray[0])
      return `${this.placeholder} (${count})`
    }
  },

  methods: {
    isActive(option) {
      return this.multiple ? this.selectedArray.includes(option) : this.modelValue === option
    },
    select(option) {
      if (this.multiple) {
        const next = this.selectedArray.includes(option)
          ? this.selectedArray.filter(o => o !== option)
          : [...this.selectedArray, option]
        this.$emit('update:modelValue', next)
        // Keep open for multi-select so several can be toggled at once
      } else {
        const val = this.modelValue === option ? '' : option
        this.$emit('update:modelValue', val)
        this.open = false
      }
    },
    selectAll() {
      this.$emit('update:modelValue', this.multiple ? [] : '')
      this.open = false
    },
    close() {
      this.open = false
    }
  }
}
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  min-width: 0;  /* grid item: allow shrink to the 1fr track instead of growing to content */
}

.filter-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;  /* clip content to the cell instead of overflowing */
  padding: 0.3rem 0.75rem;
  border: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-text) 20%, transparent);
  border-radius: 8px;
  background: transparent;
  color: var(--color-text);
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.4;
  cursor: pointer;
  text-transform: lowercase;
  white-space: nowrap;
  transition: box-shadow 0.15s;
  touch-action: manipulation;

  @media (hover: hover) {
    &:hover {
      box-shadow: 0 0 0 2px var(--color-highlight);
      color: var(--color-highlight);
    }
  }
}

.open .filter-dropdown-trigger {
  box-shadow: 0 0 0 2px var(--color-highlight);
  color: var(--color-highlight);
}

.filter-dropdown-trigger.has-value {
  box-shadow: 0 0 0 2px var(--color-accent);
  background: var(--color-accent);
  color: var(--color-accent-text, #fff);
}

/* When active (accent bg), keep readable accent-text on hover/open instead of
   switching to highlight, which would sit low-contrast on the accent fill. */
.filter-dropdown-trigger.has-value:hover,
.open .filter-dropdown-trigger.has-value {
  color: var(--color-accent-text, #fff);
  box-shadow: 0 0 0 2px var(--color-accent);
}

/* Multi-select label: take remaining space, truncate only if genuinely too tight. */
.filter-dropdown-trigger-label {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
}


.filter-dropdown-arrow {
  flex-shrink: 0;
  opacity: 0.7;
  width: 12px;
  height: 12px;
  pointer-events: none;
}

/* Single-select: ghost reserves the widest label's width; current overlays it. */
.filter-dropdown-sizer {
  position: relative;
  display: inline-block;
  margin-right: auto;  /* push the chevron to the right edge in single-select mode */
}

.filter-dropdown-ghost {
  visibility: hidden;
}

.filter-dropdown-current {
  position: absolute;
  left: 0;
  top: 0;
  white-space: nowrap;
}

/* Selection count badge: absolute, top-right corner, overlaps the trigger edge. */
.filter-dropdown-badge {
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.3rem;
  box-sizing: border-box;
  border-radius: 999px;
  background: var(--color-highlight);
  color: var(--color-highlight-text, #fff);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
}

.filter-dropdown-list {
  position: absolute;
  top: calc(100% + 0.35rem);
  left: 0;
  z-index: 100;
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
  background: var(--color-bg, #fff);
  color: var(--color-text);
  border: none;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--color-text) 20%, transparent), 0 4px 12px color-mix(in srgb, var(--color-text) 10%, transparent);
  border-radius: 8px;
  width: 100%;  /* matches the trigger's own width */
}

.filter-dropdown-item {
  padding: 0.35rem 0.85rem;
  font-size: 0.875rem;
  line-height: 1.5;  /* room for descenders (g, y, p) so they aren't clipped */
  text-transform: lowercase;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  touch-action: manipulation;

  @media (hover: hover) {
    &:hover {
      background: color-mix(in srgb, var(--color-text) 8%, var(--color-bg));
    }
  }

  &.active {
    color: var(--color-highlight);
    font-weight: 600;
  }

  &.is-multi {
    display: flex;
    align-items: flex-start;  /* checkbox stays pinned to the first line when wrapped */
    gap: 0.4rem;
    padding-right: 0.6rem;
    text-transform: none;

    &.active {
      font-weight: normal;  /* checkbox already shows selection */
    }
  }
}

.filter-dropdown-text {
  min-width: 0;  /* let this flex item shrink below its content width */
  white-space: normal;  /* wrap long labels instead of overflowing */
  overflow-wrap: break-word;  /* break mid-word only if a single word can't fit at all */
  line-height: 1.5;
}

.filter-dropdown-footer {
  margin-top: 0.35rem;
  padding-top: 0.35rem;
  border-top: 1px solid color-mix(in srgb, var(--color-text) 15%, var(--color-bg));
}

/* Optional second line under an option, e.g. a size range */
.filter-dropdown-hint {
  display: block;
  font-size: 0.8em;
  font-weight: normal;
  color: color-mix(in srgb, var(--color-text) 80%, var(--color-bg));
}

/* Square checkbox; fills with the highlight colour + a check glyph when on. */
.filter-dropdown-box {
  position: relative;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 0.35rem;  /* aligns to the text's first line */
  border: 1.5px solid color-mix(in srgb, var(--color-text) 70%, var(--color-bg));
  border-radius: 3px;
  box-sizing: border-box;
  transition: background 0.1s, border-color 0.1s;

  &.checked {
    background: var(--color-highlight);
    border-color: var(--color-highlight);

    &::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 1px;
      width: 3px;
      height: 7px;
      border: solid var(--color-highlight-text, #fff);
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}

/* Secondary action, not another option row: centered, muted, smaller. */
.filter-dropdown-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  margin-top: 0.25rem;
  padding-top: 0.45rem;
  padding-bottom: 0.15rem;
  border-top: 1px solid color-mix(in srgb, var(--color-text) 12%, var(--color-bg));
  color: color-mix(in srgb, var(--color-text) 90%, var(--color-bg));
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.1s;

  @media (hover: hover) {
    &:hover {
      background: transparent;
      color: var(--color-text);
    }
  }
}

.filter-dropdown-clear-x {
  display: inline-flex;
  justify-content: center;
  font-size: 0.9rem;
  line-height: 1;
}
</style>
